import { EditorState, LexicalEditor } from "lexical";
import {
  InitialConfigType,
  LexicalComposer,
} from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import ImagePlugin from "./imagePlugin/ImagePlugin";
import { ImageNode } from "./imagePlugin/ImageNode";

import {
  HEADING,
  QUOTE,
  UNORDERED_LIST,
  CHECK_LIST,
  ORDERED_LIST,
  HIGHLIGHT,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  STRIKETHROUGH,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  LINK,
} from "@lexical/markdown";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import editorTheme from "./EditorTheme";

import "./Editor.scss";
import ToolbarPlugin from "./toolbar/ToolbarPlugin";
import { LoadInitialContent } from "./LoadInitialContent";
import { applyHtmlToRichContentPatches } from "./applyHtmlToRichContentPatches";

const NO_CODE_TRANSFORMERS = [
  HEADING,
  QUOTE,
  UNORDERED_LIST,
  CHECK_LIST,
  ORDERED_LIST,
  HIGHLIGHT,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  STRIKETHROUGH,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  LINK,
];

type EditorProps = {
  initialContent: any;
  onChange: (editorState: EditorState, editor: LexicalEditor) => void;
};

applyHtmlToRichContentPatches();

const Editor = (props: EditorProps) => {
  const editorConfig: InitialConfigType = {
    namespace: "Editor",
    theme: editorTheme,
    onError(error) {
      throw error;
    },
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      LinkNode,
      AutoLinkNode,
      ImageNode,
    ],
  };

  const placeholder = () => {
    return <div className="editor-placeholder"></div>;
  };

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <LoadInitialContent initialContent={props.initialContent} />
      <div className="editor-container">
        <ToolbarPlugin />
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-input" />}
            placeholder={placeholder}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin onChange={props.onChange} />
          <HistoryPlugin />
          <LinkPlugin />
          <ListPlugin />
          <MarkdownShortcutPlugin transformers={NO_CODE_TRANSFORMERS} />
          <ImagePlugin />
          {/* TODO: Style <table> elements in Editor */}
          <TablePlugin />
        </div>
      </div>
    </LexicalComposer>
  );
};

export default Editor;
